/**
 * useAgents — shared context for agents + chains
 *
 * Place at: src/context/AgentsContext.jsx
 *
 * Wrap in app root (alongside AIProvider):
 *   <AIProvider>
 *     <AgentsProvider>
 *       <App />
 *     </AgentsProvider>
 *   </AIProvider>
 *
 * Consume in any page:
 *   const { agents, chains, createAgent, deleteChain, saveFlow, ... } = useAgents();
 */

import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useAI } from './AIContext';
import { customAgentAPI } from "../services/api";
import toast from "react-hot-toast";
import { lyraAPI } from '../services/lyraAPI';

const AgentsContext = createContext(null);

export function AgentsProvider({ children }) {
  const [agents, setAgents] = useState([]);
  const [chains, setChains] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(true);
  const [loadingChains, setLoadingChains] = useState(true);
  const { selectedModelId } = useAI();

  // ── Initial load ────────────────────────────────────────────────────────
  useEffect(() => {
    loadAgents();
    loadChains();
  }, []);

  const loadAgents = useCallback(async () => {
    // AgentsProvider is mounted app-wide (index.js), outside the router, so
    // it's alive on the public Landing/Login page too — skip the fetch (and
    // the error toast below) when there's no session to fetch for.
    if (!localStorage.getItem("authToken")) { setLoadingAgents(false); return; }
    setLoadingAgents(true);
    try {
      const res = await customAgentAPI.list();
      // Preserve isCustom / isBuiltIn flags from server; only tag user-created agents as isCustom
      setAgents((res.agents || []).map(a => ({
        ...a,
        isCustom: a.isCustom !== false,   // built-ins come back as false
        isBuiltIn: !!a.isBuiltIn,
      })));
    } catch {
      toast.error("Failed to load agents");
    } finally {
      setLoadingAgents(false);
    }
  }, []);

  const loadChains = useCallback(async () => {
    if (!localStorage.getItem("authToken")) { setLoadingChains(false); return; }
    setLoadingChains(true);
    try {
      const res = await customAgentAPI.listChains();
      setChains((res.chains || []).map(c => ({ ...c, isCustom: true })));
    } catch {
      toast.error("Failed to load chains");
    } finally {
      setLoadingChains(false);
    }
  }, []);

  // ── Agent CRUD ───────────────────────────────────────────────────────────
  const createAgent = useCallback(async (data) => {
    const res = await customAgentAPI.create(data);
    const newAgent = { ...res.agent, isCustom: true };
    setAgents(prev => [newAgent, ...prev]);
    return newAgent;
  }, []);

  const updateAgent = useCallback(async (id, data) => {
    const res = await customAgentAPI.update(id, data);
    const updated = { ...res.agent, isCustom: true };
    setAgents(prev => prev.map(a => (a._id || a.id) === id ? updated : a));
    return updated;
  }, []);

  const deleteAgent = useCallback(async (id) => {
    if (!window.confirm("Delete this agent?")) return false;
    try {
      await customAgentAPI.remove(id);
      setAgents(prev => prev.filter(a => (a._id || a.id) !== id));
      toast.success("Agent deleted");
      return true;
    } catch {
      toast.error("Failed to delete agent");
      return false;
    }
  }, []);

  // Called by AddAgentWizard onCreated — handles both create and update
  const handleAgentSaved = useCallback((savedAgent, isEdit = false) => {
    const tagged = { ...savedAgent, isCustom: true };
    if (isEdit) {
      setAgents(prev => prev.map(a =>
        (a._id || a.id) === (savedAgent._id || savedAgent.id) ? tagged : a
      ));
    } else {
      setAgents(prev => [tagged, ...prev]);
    }
  }, []);

  // ── Chain CRUD ───────────────────────────────────────────────────────────
  const createChain = useCallback(async (data) => {
    const res = await customAgentAPI.createChain(data);
    const newChain = { ...res.chain, isCustom: true };
    setChains(prev => [newChain, ...prev]);
    return newChain;
  }, []);

  const updateChain = useCallback(async (id, data) => {
    const res = await customAgentAPI.updateChain(id, data);
    const updated = { ...res.chain, isCustom: true };
    setChains(prev => prev.map(c => (c._id || c.id) === id ? updated : c));
    return updated;
  }, []);

  const deleteChain = useCallback(async (id) => {
    try {
      await customAgentAPI.removeChain(id);
      setChains(prev => prev.filter(c => (c._id || c.id) !== id));
      toast.success("Chain deleted");
      return true;
    } catch {
      toast.error("Failed to delete chain");
      return false;
    }
  }, []);

  // Called by AddChainWizard onCreated
  const handleChainSaved = useCallback((savedChain) => {
    const tagged = { ...savedChain, isCustom: true };
    setChains(prev => {
      const exists = prev.find(c => (c._id || c.id) === (savedChain._id || savedChain.id));
      if (exists) return prev.map(c => (c._id || c.id) === (savedChain._id || savedChain.id) ? tagged : c);
      return [tagged, ...prev];
    });
  }, []);

    // Inside the context provider state:
  const [lyraRepos, setLyraRepos] = useState([]);
  
  // Inside your existing fetchAll / useEffect that loads agents:
  const loadLyraRepos = async () => {
    try {
      const repos = await lyraAPI.getRepos();
      setLyraRepos(repos || []);
    } catch (err) {
      console.error('[AgentsContext] lyraRepos load failed', err);
    }
  };
  
  // Add to context value:
  const contextValue = {
    // ...existing values...
    lyraRepos,
    setLyraRepos,
    refreshLyraRepos: loadLyraRepos,
  };

  // ── Flow canvas save (FlowBuilderPage) ───────────────────────────────────
  /**
   * saveFlow — creates or updates a chain with source:"canvas"
   * @param {string}   flowName     - display name
   * @param {Array}    nodes        - FlowBuilderPage node array
   * @param {Array}    connections  - FlowBuilderPage connection array
   * @param {string|null} existingId - if editing an existing flow
   * @returns {{ chain, isNew }}
   */
  const saveFlow = useCallback(async (flowName, nodes, connections, existingId = null) => {
    try {
      // Sanitize nodes: ensure model is present and remove non-serializable fields from item
      const sanitizedNodes = (nodes || []).map(n => {
        let model = n.model || undefined;
        if (!model && (n.agid || '').startsWith('custom:')) {
          const aid = (n.agid || '').replace('custom:', '');
          const found = agents.find(a => (a._id || a.id) === aid);
          if (found && (found.defaultModel || found.model)) model = found.defaultModel || found.model;
        }
        if (!model) model = selectedModelId || undefined;
        const safeItem = n.item ? { id: n.item.id, name: n.item.name, tag: n.item.tag, color: n.item.color } : undefined;
        return { ...n, model, item: safeItem };
      });

      let res;
      if (existingId) {
        res = await customAgentAPI.updateFlow(existingId, flowName, sanitizedNodes, connections);
        const updated = res.chain;
        setChains(prev => prev.map(c => (c._id || c.id) === existingId ? updated : c));
        toast.success("Flow updated!");
        return { chain: updated, isNew: false };
      } else {
        res = await customAgentAPI.saveFlow(flowName, sanitizedNodes, connections);
        const newChain = res.chain;
        setChains(prev => [newChain, ...prev]);
        toast.success("Flow saved to Chains!");
        return { chain: newChain, isNew: true };
      }
    } catch (err) {
      toast.error("Failed to save flow");
      throw err;
    }
  }, [agents, selectedModelId]);

  /** deleteFlow — removes a canvas chain by Mongo _id */
  const deleteFlow = useCallback(async (id) => {
    try {
      await customAgentAPI.removeChain(id);
      setChains(prev => prev.filter(c => (c._id || c.id) !== id));
      toast.success("Flow deleted");
      return true;
    } catch {
      toast.error("Failed to delete flow");
      return false;
    }
  }, []);

  // ── Helpers ──────────────────────────────────────────────────────────────
  /** wizard chains only — for SpecializedAgentsPage chain runner */
  const wizardChains = chains.filter(c => c.source !== "canvas");
  /** canvas flows only — for FlowBuilderPage list */
  const canvasFlows = chains.filter(c => c.source === "canvas");
  /** all chains including canvas — for AgentStudioPage */
  const allChains = chains;

  return (
    <AgentsContext.Provider value={{
      // state
      agents,
      chains,
      allChains,
      wizardChains,
      canvasFlows,
      loadingAgents,
      loadingChains,
      // agent actions
      loadAgents,
      createAgent,
      updateAgent,
      deleteAgent,
      handleAgentSaved,
      // chain actions
      loadChains,
      createChain,
      updateChain,
      deleteChain,
      handleChainSaved,
      // canvas flow
      saveFlow,
      deleteFlow,
    }}>
      {children}
    </AgentsContext.Provider>
  );
}

export const useAgents = () => {
  const ctx = useContext(AgentsContext);
  if (!ctx) throw new Error("useAgents must be used within AgentsProvider");
  return ctx;
};
